// How often parents use the console, counted by Umami — and nothing about the
// children.
//
// # Who is counted
//
// A parent: on the pages before a device is paired (signing in, founding a
// family, the two mailed links) and under /eltern. Never a child: not while a
// child is signed in, not on the profile picker the tablet boots to, not in a
// session or a Rätsel. A page that is not on that list does not load the
// script, so a tablet only ever used by children never fetches it.
//
// # What is sent
//
// The script runs with automatic tracking off, so it sends nothing of its own
// accord: every page view is sent from here, with a path from a fixed list.
// An id in the path becomes `:id`, the query string and the hash are dropped,
// the title is the app's name rather than whatever a page set, and the
// referrer is cut to its origin. A name, an id or a mailed token cannot reach
// Umami by any of those routes.
//
// # When nothing happens at all
//
// Without both VITE_UMAMI_WEBSITE_ID and VITE_UMAMI_SRC the build loads no
// script and sends nothing — locally, in e2e and in a preview. The script's
// own data-domains does the same for a build that has them but runs anywhere
// but app.socrates-tutor.com, and data-do-not-track honours the browser's
// setting. No cookie is set either way; Umami does not use them.

import type { Who } from './session';

/** The host Umami counts on. A build served from anywhere else sends nothing. */
export const ANALYTICS_DOMAIN = 'app.socrates-tutor.com';

/** Where the person at the device is, as far as counting is concerned. */
export interface AnalyticsState {
  pathname: string;
  /** False until the device knows its family. */
  paired: boolean;
  /** Signed in, but with no family to pair to yet. */
  needsFamily: boolean;
  who: Who;
}

// The parent's pages, by the path they are counted under. A path under
// /eltern that is not here is not counted rather than counted raw.
const PARENT_PATHS = new Set([
  '/eltern',
  '/eltern/kinder',
  '/eltern/lehrplan',
  '/eltern/arbeitsblaetter',
  '/eltern/abo',
  '/eltern/sicherheit',
]);

// The mailed links, mounted on both sides of the pairing (App.tsx).
const MAILED_PATHS = new Set(['/adresse-bestaetigen', '/passwort-setzen']);

/**
 * The path a page view is counted under, or null when it is not counted.
 * Null for every page a child sees.
 */
export function analyticsPath({ pathname, paired, needsFamily, who }: AnalyticsState): string | null {
  if (who.kind === 'child') return null;
  const path = pathname.replace(/\/+$/, '') || '/';

  if (MAILED_PATHS.has(path)) return path;
  if (!paired) {
    // Every other path on an unpaired device renders one of two screens,
    // whatever the address bar says. The sign-in's own callback is a redirect
    // carrying a code, and is not a page anybody reads.
    if (path === '/auth/callback') return null;
    return needsFamily ? '/familie-anlegen' : '/anmelden';
  }

  if (PARENT_PATHS.has(path)) return path;
  if (/^\/eltern\/kinder\/[^/]+$/.test(path)) return '/eltern/kinder/:id';
  return null;
}

/** Where the script comes from and which website it counts for. */
export interface AnalyticsConfig {
  websiteId?: string;
  src?: string;
}

interface UmamiPayload {
  url?: string;
  referrer?: string;
  title?: string;
  [key: string]: unknown;
}

interface Umami {
  track(build: (props: UmamiPayload) => UmamiPayload): void;
}

type UmamiWindow = Window & { umami?: Umami };

/** Counts one page view; a null path counts nothing and loads nothing. */
export type PageView = (path: string | null) => void;

/**
 * A page-view sender over one document. Inert when the config is incomplete:
 * it then never touches the document.
 */
export function createAnalytics(config: AnalyticsConfig, doc: Document = document): PageView {
  const { websiteId, src } = config;
  if (!websiteId || !src) return () => {};

  const win = doc.defaultView as UmamiWindow | null;
  let script: HTMLScriptElement | null = null;
  // Page views from before the script finished loading, sent once it has.
  let pending: string[] = [];

  const send = (path: string) => {
    win?.umami?.track((props) => ({
      ...props,
      url: path,
      title: 'Sokrates',
      referrer: originOf(doc.referrer),
    }));
  };

  const load = () => {
    script = doc.createElement('script');
    script.defer = true;
    script.src = src;
    script.dataset.websiteId = websiteId;
    script.dataset.domains = ANALYTICS_DOMAIN;
    script.dataset.doNotTrack = 'true';
    script.dataset.autoTrack = 'false';
    script.dataset.excludeSearch = 'true';
    script.dataset.excludeHash = 'true';
    script.addEventListener('load', () => {
      const queued = pending;
      pending = [];
      queued.forEach(send);
    });
    doc.head.appendChild(script);
  };

  return (path) => {
    if (path === null) return;
    if (!script) load();
    if (win?.umami) send(path);
    else pending.push(path);
  };
}

function originOf(referrer: string): string {
  if (!referrer) return '';
  try {
    return new URL(referrer).origin;
  } catch {
    return '';
  }
}

/** The console's own sender, configured at build time. */
export const pageView: PageView = createAnalytics({
  websiteId: import.meta.env.VITE_UMAMI_WEBSITE_ID as string | undefined,
  src: import.meta.env.VITE_UMAMI_SRC as string | undefined,
});
